import React from 'react';
import { Box, Button, Typography, Container, Paper, Link } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [statusMsg, setStatusMsg] = React.useState('');

    // AUTO-REDIRECT IF LOGGED IN
    React.useEffect(() => {
        if (!loading && user) {
            navigate('/', { replace: true });
        }
    }, [user, loading, navigate]);

    // Check if app is running in an iframe (Whop context)
    const inIframe = window.self !== window.top;

    const handleLogin = () => {
        if (inIframe) {
            setStatusMsg('Re-establishing session...');
            // In Whop Hub: Refresh current page to trigger middleware token capture
            window.location.href = '/';
        } else {
            setStatusMsg('Redirecting to Whop Hub...');
            // Outside Whop Hub: Redirect to the official Experience URL
            window.location.href = 'https://whop.com/joined/gammaflow/exp_oZD0AOLRKS0e9D/app/';
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#0a0e17',
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={24}
                    sx={{
                        p: 6,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        background: 'rgba(19, 26, 43, 0.8)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0, 212, 170, 0.2)',
                        borderRadius: 4,
                    }}
                >
                    <Box
                        component="img"
                        src="/assets/logo.png"
                        alt="GammaFlow Logo"
                        sx={{
                            width: 80,
                            height: 80,
                            mb: 2,
                            filter: 'drop-shadow(0 0 10px rgba(0, 212, 170, 0.3))'
                        }}
                    />
                    <Typography
                        variant="h3"
                        gutterBottom
                        sx={{
                            fontWeight: 900,
                            letterSpacing: '-0.02em',
                            background: 'linear-gradient(90deg, #00d4aa 0%, #7b68ee 50%, #ff6b6b 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '0 0 30px rgba(0, 212, 170, 0.2)'
                        }}
                    >
                        GammaFlow
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{ color: '#8b9bb4', mb: 4, textAlign: 'center' }}
                    >
                        Institutional Flow Visualization Terminal
                    </Typography>

                    {statusMsg && (
                        <Typography
                            variant="body1"
                            sx={{
                                color: statusMsg.includes('Please open') ? '#ff6b6b' : '#00d4aa',
                                mb: 3,
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            {statusMsg}
                        </Typography>
                    )}

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        onClick={handleLogin}
                        sx={{
                            py: 2,
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #00d4aa 0%, #00a382 100%)',
                            '&:hover': {
                                background: 'linear-gradient(90deg, #00f2c3 0%, #00d4aa 100%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 10px 20px rgba(0, 212, 170, 0.3)',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        LAUNCH FROM WHOP
                    </Button>

                    <Typography
                        variant="body2"
                        sx={{ mt: 4, color: '#4a5568', textAlign: 'center' }}
                    >
                        Access is managed automatically via Whop.<br />
                        Please ensure you have an active access pass.
                    </Typography>


                    <Typography
                        variant="caption"
                        sx={{ mt: 3, color: '#64748b', textAlign: 'center' }}
                    >
                        By signing in, you agree to our{' '}
                        <Link component={RouterLink} to="/terms" sx={{ color: '#00d4aa' }}>
                            Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link component={RouterLink} to="/privacy" sx={{ color: '#00d4aa' }}>
                            Privacy Policy
                        </Link>
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
